import type { DockCommand } from '@/types/dock';

/**
 * The words on the keys an operator presses, and on the dialogs that ask them to mean it.
 *
 * This is the highest-stakes copy in the product: #505's evaluator sat in front of the COP and
 * could not work out how to fly, and this bar is how flying happens. It is also the densest
 * cluster of wording that was argued about on purpose - `DroneCommandBar.tsx:39-145` runs to a
 * hundred lines of justification - so every change here either preserves the fact its comment
 * records, or does not happen. The comments moved with the strings.
 *
 * CASE. Both bars render their keys through a CSS `uppercase` class
 * (`DroneCommandBar.tsx:641`, `DockSidebar.tsx:160`), so these are sentence case in source and
 * unchanged on screen.
 */

/**
 * The flight keys, in the fixed left-to-right order the bar renders them: prep, launch,
 * in-flight, recover. The positions do not move, because a key that moves is a key an operator
 * has to look for.
 */
/** The key labels that carry their own capitals rather than borrowing the CSS; see `arm`. */
export const SHOUTING_COMMAND_KEYS: ReadonlySet<string> = new Set(['ARM', 'DISARM']);
export const COMMAND_KEY_LABEL = {
  /**
   * Kept as "Arm", not expanded.
   *
   * It is the word on the RC, the word the companion app signs with, and the word an operator
   * says out loud. Rule 5 of `docs/OPERATOR-LANGUAGE.md` retires abbreviations that have to be
   * decoded; this is not one, it is a verb.
   *
   * Capitals in the string, not just from the CSS, at Kosta's call - it is the marking on the
   * controller. Named in the guard test's allowed-shouts set so the case rule stays enforced
   * rather than merely relaxed.
   */
  arm: 'ARM',
  disarm: 'DISARM',
  takeoff: 'Takeoff',
  /**
   * Was `GO-TO`, which is our word for it and nobody else's. The key arms a map click, and
   * what happens after that click is that the drone flies there.
   */
  goto: 'Fly here',
  /**
   * Kept as "Manual".
   *
   * #505's T8 table proposed "Fly by hand". Rejected on two grounds: "manual" is not jargon -
   * it is the ordinary English word for the thing, and it is what an operator says when they
   * ask for it - and the alternative is nearly twice as wide on a bar whose key positions are
   * deliberately fixed for muscle memory.
   */
  manual: 'Manual',
  /**
   * Was `HOLD`.
   *
   * Confirmed by Kosta as a pair with the "Resume" sitting next to it: the action is called
   * `pause`, it pauses a mission, and the key says so.
   */
  pause: 'Pause',
  resume: 'Resume',
  /**
   * Was `RTH`.
   *
   * The abbreviation survives elsewhere (it is on the allowlist, and it is what telemetry and
   * the aircraft's own logs call it), but not on the key itself. This is the red button a
   * police operator has to understand at a glance, and "RTH" is exactly the kind of thing
   * #505 was filed about.
   */
  return_home: 'Return home',
  land: 'Land',
  confirm_landing: 'Confirm landing',
  /**
   * Kept as "Abort".
   *
   * Sentence case only. "Stop and hold" was tried - it is what the key's own comment says the
   * outcome is - and rejected at Kosta's call: "Abort" is the word the crew uses for it.
   */
  abort: 'Abort',
} as const;

/**
 * The "skip the dock" escapes for LAND and RETURN HOME - not siblings any more (#586).
 *
 * `land` / `landHint` are still offered proactively, inside LAND's own confirm dialog: what
 * "here" skips is never obvious from the word alone, so the answer lives in the hint beside it
 * rather than in a longer label. The normal path opens the bay and waits for the board to
 * confirm before the drone is told anything, and this one does not.
 *
 * `emergencyRth` / `emergencyRthHint` are RETURN HOME's escape, and #586 moved it: it used to
 * sit beside RETURN HOME under the name "Return directly", offered before any refusal ever
 * happened, and a real flight took that path with the dock choreography never asked to run.
 * It now appears only after the orchestrated return is refused, and only when that refusal
 * actually permits it (`postFailureOf('return_home')?.code === 'landing_sequence_refused'` -
 * a refusal with a bay sequence genuinely running on the dock gets a different code on purpose
 * and never offers this at all, see `src/api/drones.ts`).
 *
 * Not called "Emergency land": this bar already has LAND and LAND's own "Land here" escape,
 * both of which mean "come down where the drone is, right now" - this does the opposite, it
 * flies the drone HOME. "RTH" is deliberately kept (it is on the allowlist, `terms.ts`) so the
 * label reads as a variant of returning home, not a variant of landing. Sends the same
 * `return_home` with `direct: true` underneath (D1 in the implementation record,
 * `docs/PLANS/collapse-rth-single-flow/`).
 */
export const DIRECT_VARIANT = {
  land: 'Land here',
  landHint: 'Descend where the drone is now. No bay sequence, no waiting for the dock.',
  emergencyRth: 'Emergency RTH',
  emergencyRthHint: "The drone's own RTH, with no bay sequence and no hold over the dock. "
    + 'Only appears once the orchestrated return has been refused, and only when the refusal '
    + 'says it is safe to skip.',
} as const;

/**
 * What each key will do, said before the operator commits to it.
 *
 * Each sentence is completed by the callsign in front of it, so it reads
 * "EAGLE-01 will arm and climb...". The facts each one carries were checked against the
 * command's own behaviour and none were dropped in the rename.
 */
export const CONFIRM_COPY: Readonly<Record<string, string>> = {
  takeoff: 'will arm and climb to its takeoff altitude.',
  return_home:
    'will climb to its return altitude and fly home. Over its home dock the bay is opened and '
    + 'the landing released once it arrives. If that is refused, an emergency option to skip '
    + 'it and fly home directly may appear.',
  // Over its own dock, LAND is a dock recovery: the server opens the bay and waits for the dock
  // to confirm it before the drone is told anything (golden-flow steps 13-14). Anywhere else it
  // is exactly what it says.
  land:
    'will descend and land. Over its home dock the bay is opened and confirmed first. '
    + '"Land here" skips that and brings it down where it is now.',
  // The operator is ASSERTING something about the world, not just repeating an order: DJI's
  // vision system refused the touchdown and this overrides that refusal. So the copy names the
  // assertion rather than the verb.
  confirm_landing:
    'will complete its descent and touch down. You are confirming the ground below the drone is '
    + 'clear and safe to land on.',
};

/** The dialog that asks. Was "Confirm command", which named the mechanism, not the moment. */
export const CONFIRM_DIALOG = {
  droneTitle: 'Send this command?',
  dockTitle: 'Send this command?',
  /** Was "This action cannot be undone." - true, but it does not say what to weigh. */
  irreversible: 'This cannot be undone once the dock accepts it.',
  cancel: 'Cancel',
  send: 'Send',
} as const;

/**
 * What the ARM key says when it is the release for a command the companion app is holding.
 *
 * Per verb, because "authorise the launch" is a false description of a fly-here on a drone that
 * is already in the air.
 */
export const HELD_ARM_PROMPT = {
  takeoff: 'Takeoff is held. Press ARM to authorise the launch.',
  goto: 'The move is held. Press ARM to authorise it.',
} as const;

/**
 * The dock's own commands.
 *
 * The confirmation dialog used to render the MQTT method name through
 * `replace(/_/g, ' ').toUpperCase()`, so an operator was asked to confirm
 * "Send TURN OFF DRONES to DOCK-01?" - a question written by the protocol. Every command whose
 * name was only ever protocol shape still reads as words here.
 *
 * THE SIX BOARD COMMANDS BELOW ARE THE EXCEPTION, AND THEY NAME THEIR WIRE VERB ON PURPOSE.
 *
 * `#528` renamed the four flight phases by WHEN they run - "Before takeoff", "After landing" -
 * on the reasoning that a phase is clearer named by its moment. That is right for a control an
 * operator uses to fly. It is wrong for these, and the founder's call on 2026-09-05 was to put
 * the wire verbs back, because of what this row actually is:
 *
 *   - These buttons exist to EXERCISE THE BOARD DIRECTLY for testing - `DockSidebar` says so in
 *     place. The launch sequence drives the same commands itself; a person pressing one is
 *     bench-testing hardware, not flying.
 *   - The reader is therefore holding `docs/GOLDEN-FLOW.md`, `MQTT_KSA.py` or an AirScort log,
 *     all of which say `pre_takeoff` and `tray_open`. A button reading "Before takeoff" cannot
 *     be matched against a log line reading `pre_takeoff` without a translation step, and that
 *     step is performed by a human next to a powered aircraft.
 *   - The four phases are AirScort's vocabulary, not ours. `DockSidebar`'s own comment says they
 *     are deliberately that vocabulary and must never be called "Arm", which is the aircraft's
 *     signed verb. Keeping the vendor's word is what keeps that line visible.
 *
 * So: named by the verb, and `DOCK_COMMAND_EFFECT` carries the plain-English meaning as a hint
 * for the two the code can actually vouch for.
 *
 * DELIBERATELY NOT `tray_open` / `tray_close`, though the same argument would apply to them.
 * Those two entries are unreachable: neither `CONFIRM_COMMANDS` set contains them
 * (`DockSidebar.tsx`, `DockCommandBar.tsx`), and the confirm dialog is the only thing that
 * renders this map, so the lid buttons carry their own text - "Open" / "Close" under the Bay
 * row, "Open Lid" in the command bar. Renaming them here would put the retired word "tray" back
 * in the copy module, and buy an exemption in the vocabulary guard, to change nothing on screen.
 * If the lid buttons are ever single-sourced from this map, revisit it then and with a render
 * test.
 */
export const DOCK_COMMAND_LABEL: Readonly<Record<DockCommand, string>> = {
  tray_open: 'Open the bay',
  tray_close: 'Close the bay',
  pre_takeoff: 'Pre-takeoff',
  post_takeoff: 'Post-takeoff',
  pre_land: 'Pre-land',
  post_land: 'Post-land',
  emergency_mode_on: 'Emergency stop',
  emergency_mode_clear: 'Clear the emergency stop',
  rc_power: 'Controller power',
  drone_power_1: 'Power to bay 1',
  drone_power_2: 'Power to bay 2',
  drone_power_3: 'Power to bay 3',
  led_on: 'Light on',
  led_off: 'Light off',
  turn_off_drones: 'Power off every drone',
  drone_service: 'Service mode',
  set_drone_power: 'Set drone power',
  resume: 'Resume',
  abort: 'Stop and hold',
};

/**
 * What the board does, for the two phases the code can actually vouch for.
 *
 * Deliberately not filled in for `post_takeoff` and `pre_land`: nothing in this repo documents
 * their effect, and a hint that guesses is worse than no hint on a control that moves hardware
 * under a drone.
 */
export const DOCK_COMMAND_EFFECT: Readonly<Partial<Record<DockCommand, string>>> = {
  pre_takeoff: 'Extends the bay and powers the drone.',
  post_land: 'Closes the bay, and swaps the battery on docks that can.',
};

/**
 * The dock sidebar's control-row headings for the two phase pairs.
 *
 * "phase", not "steps": these are the board's own lifecycle phases, and the row sits directly
 * above buttons named for the wire verbs. A heading that says "steps" reads as a sequence the
 * operator is expected to walk, which is exactly what this row is NOT - the launch sequence
 * drives these itself.
 */
export const DOCK_PHASE_ROW = {
  takeoff: 'Takeoff phase',
  landing: 'Landing phase',
} as const;

